import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { BACKEND_URL } from '../utils/backendURL';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Card } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';
import { 
  ArrowLeft,
  Play,
  Save,
  Database,
  Plus,
  Camera,
  Trash2,
  Edit,
  GripVertical,
  ChevronRight,
  ChevronDown,
  Globe,
  MousePointer,
  Type,
  CheckCircle,
  Eye,
  Clock,
  Code,
  Copy,
  RefreshCw,
  Settings,
  Folder,
  X,
  Target,
  AlertTriangle,
  Zap,
  Monitor,
  Sparkles
} from 'lucide-react';

const FlowTestEditor = ({ 
  test, 
  onClose, 
  onSave, 
  onQuickRun,
  onManageVariables,
  onVariablesUpdate,
  projects = [],
  testSegments = [],
  loading = false,
  onInsertSegment,
  onCreateSegmentFromSteps,
  externalVariables,
  onResumeVNCRecording
}) => {
  // Form state
  const [name, setName] = useState(test?.name || '');
  const [description, setDescription] = useState(test?.description || '');
  const [projectId, setProjectId] = useState(test?.project_id || '');
  const [steps, setSteps] = useState(test?.steps || []);
  const [selectedStepIndex, setSelectedStepIndex] = useState(null);
  const [testVariables, setTestVariables] = useState(test?.test_variables || {});
  
  // Sync steps when test prop changes (e.g., after inserting a segment)
  React.useEffect(() => {
    if (test?.steps) {
      setSteps(test.steps);
    }
  }, [test?.steps]);
  
  // Sync external variables when they change
  React.useEffect(() => {
    if (externalVariables !== undefined) {
      setTestVariables(externalVariables);
    }
  }, [externalVariables]);
  
  // Add step modal state
  const [showAddStepModal, setShowAddStepModal] = useState(false);
  const [newStepPrompt, setNewStepPrompt] = useState('');
  const [addingStep, setAddingStep] = useState(false);
  
  // Segment selection state
  const [selectedStepsForSegment, setSelectedStepsForSegment] = useState([]);

  // Get the currently selected step
  const selectedStep = selectedStepIndex !== null ? steps[selectedStepIndex] : null;

  // Step type icons
  const getStepIcon = (step) => {
    const type = step?.type?.toLowerCase() || step?.action?.toLowerCase() || '';
    if (type.includes('navigate') || type.includes('navigation') || type.includes('goto')) return <Globe className="h-4 w-4 text-blue-500" />;
    if (type.includes('click')) return <MousePointer className="h-4 w-4 text-green-500" />;
    if (type.includes('type') || type.includes('fill') || type.includes('input')) return <Type className="h-4 w-4 text-purple-500" />;
    if (type.includes('assert') || type.includes('verify') || type.includes('expect')) return <CheckCircle className="h-4 w-4 text-orange-500" />;
    if (type.includes('wait')) return <Clock className="h-4 w-4 text-yellow-500" />;
    if (type.includes('screenshot')) return <Camera className="h-4 w-4 text-pink-500" />;
    return <Code className="h-4 w-4 text-gray-500" />;
  };

  // Get step description
  const getStepDescription = (step) => {
    if (step.description) return step.description;
    if (step.action === 'navigate' || step.type === 'navigation') return `Navigate to ${step.url || step.value || step.target_url || 'URL'}`;
    if (step.action === 'click') return `Click on ${step.selector?.value || step.selector || 'element'}`;
    if (step.action === 'type' || step.action === 'fill') return `Type "${(step.value || '').substring(0, 20)}..."`;
    if (step.type === 'assertion' || step.action?.startsWith('assert_')) {
      const expectedPreview = step.expected_text ? `"${step.expected_text.substring(0, 30)}${step.expected_text.length > 30 ? '...' : ''}"` : '';
      return `Assert ${step.assertion_type || 'text'}${expectedPreview ? `: ${expectedPreview}` : ''}`;
    }
    return step.action || step.type || 'Step';
  };

  // Handle step deletion
  const handleDeleteStep = (index) => {
    const newSteps = [...steps];
    newSteps.splice(index, 1);
    setSteps(newSteps);
    if (selectedStepIndex === index) {
      setSelectedStepIndex(null);
    } else if (selectedStepIndex > index) {
      setSelectedStepIndex(selectedStepIndex - 1);
    }
    // Update segment selection
    setSelectedStepsForSegment(prev => 
      prev.filter(i => i !== index).map(i => i > index ? i - 1 : i)
    );
  };

  // Handle step update
  const handleUpdateStep = (index, field, value) => {
    const newSteps = [...steps];
    newSteps[index] = { ...newSteps[index], [field]: value };
    setSteps(newSteps);
  };

  // Handle nested selector update
  const handleUpdateSelector = (index, field, value) => {
    const newSteps = [...steps];
    const step = newSteps[index];
    if (!step.selector || typeof step.selector === 'string') {
      step.selector = { type: 'css', value: step.selector || '', fallback_selectors: [] };
    }
    step.selector[field] = value;
    setSteps(newSteps);
  };

  // Add fallback selector
  const handleAddFallback = (index) => {
    const newSteps = [...steps];
    const step = newSteps[index];
    if (!step.selector) {
      step.selector = { type: 'css', value: '', fallback_selectors: [] };
    }
    if (!step.selector.fallback_selectors) {
      step.selector.fallback_selectors = [];
    }
    step.selector.fallback_selectors.push({ type: 'css', value: '' });
    setSteps(newSteps);
  };

  // Update fallback selector
  const handleUpdateFallback = (stepIndex, fallbackIndex, field, value) => {
    const newSteps = [...steps];
    newSteps[stepIndex].selector.fallback_selectors[fallbackIndex][field] = value;
    setSteps(newSteps);
  };

  // Remove fallback selector
  const handleRemoveFallback = (stepIndex, fallbackIndex) => {
    const newSteps = [...steps];
    newSteps[stepIndex].selector.fallback_selectors.splice(fallbackIndex, 1);
    setSteps(newSteps);
  };

  // Add new step from prompt
  const handleAddStep = async () => {
    if (!newStepPrompt.trim()) return;
    
    setAddingStep(true);
    try {
      // Call AI to convert natural language to step
      const response = await axios.post(
        `${BACKEND_URL}/api/flow-testing/convert-prompt-to-step`,
        {
          prompt: newStepPrompt.trim(),
          existing_steps: steps
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );

      const aiStep = response.data;
      
      // Check if AI returned valid data
      if (!aiStep || !aiStep.action) {
        throw new Error('AI returned invalid step data');
      }
      
      // Map AI response to the step format expected by the UI
      const isNavigationStep = aiStep.action === 'navigate' || aiStep.action === 'goto';
      const newStep = {
        id: `step_${Date.now()}`,
        action: aiStep.action,
        type: isNavigationStep ? 'navigation' : aiStep.action,
        description: aiStep.description || newStepPrompt,
        // Use 'value' field for all actions (navigate uses value for URL)
        value: aiStep.value || '',
        // Only include selector for non-navigation steps
        ...(isNavigationStep ? {} : {
          selector: aiStep.selector || { type: 'css', value: '', fallback_selectors: [] }
        }),
        // Timing
        wait_before: aiStep.wait_before || 0,
        wait_after: aiStep.wait_after || 500,
        timeout: 30000,
        // Assertions
        assertions: aiStep.assertions || [],
        order: steps.length + 1
      };
      
      setSteps([...steps, newStep]);
      setNewStepPrompt('');
      setShowAddStepModal(false);
      setSelectedStepIndex(steps.length);
    } catch (error) {
      console.error('Error adding step:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
      alert(`AI Step Generation Failed: ${errorMessage}\n\nCreating empty step instead. Please configure it manually.`);
      // Fallback to basic step if AI fails
      const fallbackStep = {
        id: `step_${Date.now()}`,
        type: 'custom',
        action: 'custom',
        description: newStepPrompt,
        prompt: newStepPrompt,
        selector: { type: 'css', value: '', fallback_selectors: [] },
        value: '',
        timeout: 30000,
        order: steps.length + 1
      };
      setSteps([...steps, fallbackStep]);
      setNewStepPrompt('');
      setShowAddStepModal(false);
      setSelectedStepIndex(steps.length);
    } finally {
      setAddingStep(false);
    }
  };

  // Add screenshot step
  const handleAddScreenshotStep = () => {
    const newStep = {
      id: `step_${Date.now()}`,
      type: 'screenshot',
      action: 'screenshot',
      description: 'Take screenshot',
      name: `screenshot_${steps.length + 1}`,
      timeout: 30000
    };
    setSteps([...steps, newStep]);
    setSelectedStepIndex(steps.length);
  };

  // Handle save
  const handleSave = () => {
    onSave({
      ...test,
      name,
      description,
      project_id: projectId || null,
      steps,
      test_variables: testVariables
    });
  };

  // Move step up
  const moveStepUp = (index) => {
    if (index === 0) return;
    const newSteps = [...steps];
    [newSteps[index - 1], newSteps[index]] = [newSteps[index], newSteps[index - 1]];
    setSteps(newSteps);
    setSelectedStepIndex(index - 1);
  };

  // Move step down
  const moveStepDown = (index) => {
    if (index === steps.length - 1) return;
    const newSteps = [...steps];
    [newSteps[index], newSteps[index + 1]] = [newSteps[index + 1], newSteps[index]];
    setSteps(newSteps);
    setSelectedStepIndex(index + 1);
  };

  // Duplicate step
  const duplicateStep = (index) => {
    const newSteps = [...steps];
    const duplicated = { ...JSON.parse(JSON.stringify(newSteps[index])), id: `step_${Date.now()}` };
    newSteps.splice(index + 1, 0, duplicated);
    setSteps(newSteps);
    setSelectedStepIndex(index + 1);
  };

  // Toggle step selection for segment
  const toggleStepSelection = (index) => {
    setSelectedStepsForSegment(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index].sort((a, b) => a - b)
    );
  };

  // Selector type examples
  const getSelectorExamples = (type) => {
    const examples = {
      css: [
        { code: 'button[type="submit"]', desc: 'Button by type' },
        { code: '#login-button', desc: 'Element by ID' },
        { code: '.btn-primary', desc: 'Element by class' },
        { code: '[data-testid="submit"]', desc: 'Element by test ID' }
      ],
      xpath: [
        { code: '//button[text()="Submit"]', desc: 'Button by text' },
        { code: '//input[@name="email"]', desc: 'Input by name' },
        { code: '//div[@class="error"]', desc: 'Div by class' }
      ],
      text: [
        { code: 'Submit', desc: 'Exact text match' },
        { code: 'Click here', desc: 'Link or button text' }
      ],
      placeholder: [
        { code: 'Enter your email', desc: 'Input placeholder' },
        { code: 'Search...', desc: 'Search field placeholder' }
      ],
      label: [
        { code: 'Email Address', desc: 'Form label text' },
        { code: 'Password', desc: 'Password field label' }
      ],
      role: [
        { code: 'button', desc: 'Any button element' },
        { code: 'textbox', desc: 'Input field' },
        { code: 'link', desc: 'Link element' }
      ]
    };
    return examples[type] || examples.css;
  };

  // Render using Portal to ensure full-page coverage
  const editorContent = (
    <div className="fixed inset-0 bg-white z-[9999] flex flex-col" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}>
      {/* Top Header Bar */}
      <div className="border-b bg-white px-6 py-3 flex-shrink-0 shadow-sm">
        <div className="flex items-center justify-between">
          {/* Left: Back button + Name */}
          <div className="flex items-center space-x-4 flex-1">
            <Button variant="ghost" size="sm" onClick={onClose} className="text-gray-600 hover:text-gray-900">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div className="h-6 w-px bg-gray-200" />
            {/* Improved editable title field */}
            <div className="flex-1 max-w-md">
              <label className="text-xs text-gray-500 mb-0.5 block">Test Name</label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter test name..."
                className="font-semibold text-lg border border-gray-300 rounded-md px-3 py-1.5 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 bg-white"
              />
            </div>
          </div>
          
          {/* Right: Action Buttons */}
          <div className="flex items-center space-x-2">
            {/* Resume VNC Recording for vnc-recorded tests */}
            {test?.tags?.includes('vnc-recorded') && onResumeVNCRecording && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => onResumeVNCRecording(test)}
                className="text-indigo-700 border-indigo-200 hover:bg-indigo-50"
              >
                <Monitor className="h-4 w-4 mr-2" />
                Resume Recording
              </Button>
            )}
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => onManageVariables(testVariables)}
              className="text-purple-700 border-purple-200 hover:bg-purple-50"
            >
              <Database className="h-4 w-4 mr-2" />
              Variables ({Object.keys(testVariables).length})
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={onClose}
            >
              Cancel
            </Button>
            
            {/* Quick Run with Dropdown */}
            <div className="flex items-center">
              <Button
                size="sm"
                onClick={() => {
                  // Get the current test data with edits
                  const updatedTest = {
                    ...test,
                    name,
                    description,
                    project_id: projectId || null,
                    steps,
                    test_variables: testVariables
                  };
                  if (onQuickRun) onQuickRun(updatedTest, 'headless');
                }}
                className="bg-purple-600 hover:bg-purple-700 text-white rounded-r-none border-r border-purple-500"
                disabled={loading || !name.trim()}
              >
                <Zap className="h-4 w-4 mr-1" />
                Quick Run
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    size="sm"
                    className="bg-purple-600 hover:bg-purple-700 text-white rounded-l-none px-2"
                    disabled={loading || !name.trim()}
                  >
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem 
                    onClick={() => {
                      const updatedTest = {
                        ...test,
                        name,
                        description,
                        project_id: projectId || null,
                        steps,
                        test_variables: testVariables
                      };
                      if (onQuickRun) onQuickRun(updatedTest, 'headless');
                    }}
                    className="cursor-pointer"
                  >
                    <div className="flex items-center justify-between w-full">
                      <div className="flex items-center gap-2">
                        <Zap className="h-4 w-4 text-green-600" />
                        <div>
                          <p className="font-medium">Headless Mode</p>
                          <p className="text-xs text-gray-500">Faster, runs in background</p>
                        </div>
                      </div>
                    </div>
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => {
                      const updatedTest = {
                        ...test,
                        name,
                        description,
                        project_id: projectId || null,
                        steps,
                        test_variables: testVariables
                      };
                      if (onQuickRun) onQuickRun(updatedTest, 'headed');
                    }}
                    className="cursor-pointer"
                  >
                    <div className="flex items-center justify-between w-full">
                      <div className="flex items-center gap-2">
                        <Eye className="h-4 w-4 text-blue-600" />
                        <div>
                          <p className="font-medium">Headed Mode</p>
                          <p className="text-xs text-gray-500">Visual test, see browser</p>
                        </div>
                      </div>
                    </div>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            
            <Button 
              size="sm"
              onClick={handleSave}
              disabled={loading || !name.trim()}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {loading ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Update Test
            </Button>
          </div>
        </div>
        
        {/* Second Row: Description + Project */}
        <div className="flex items-center space-x-4 mt-3">
          <div className="flex-1">
            <label className="text-xs text-gray-500 mb-0.5 block">Description</label>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Add a description for this test..."
              className="text-sm text-gray-600 border-gray-200"
            />
          </div>
          {projects.length > 0 && (
            <div className="flex items-center space-x-2">
              <Folder className="h-4 w-4 text-purple-600" />
              <select
                className="text-sm p-2 border border-gray-200 rounded-md bg-white min-w-[180px]"
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
              >
                <option value="">No Project</option>
                {projects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.icon} {project.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar: Steps List */}
        <div className="w-96 border-r bg-gray-50 flex flex-col">
          {/* Sidebar Header */}
          <div className="p-4 border-b bg-white">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-900">Test Steps</h3>
              <Badge variant="secondary" className="bg-blue-100 text-blue-700">{steps.length} steps</Badge>
            </div>
            <div className="flex space-x-2">
              <Button 
                size="sm" 
                variant="outline" 
                className="flex-1"
                onClick={() => setShowAddStepModal(true)}
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Step
              </Button>
              <Button 
                size="sm" 
                variant="outline"
                onClick={handleAddScreenshotStep}
                title="Add Screenshot Step"
              >
                <Camera className="h-4 w-4" />
              </Button>
            </div>
            
            {/* Segment Selection Info */}
            {selectedStepsForSegment.length > 0 && (
              <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-green-700">
                    {selectedStepsForSegment.length} step(s) selected
                  </span>
                  <div className="flex items-center space-x-2">
                    {onCreateSegmentFromSteps && (
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="text-green-700 border-green-300 hover:bg-green-100 h-6 px-2"
                        onClick={() => onCreateSegmentFromSteps(selectedStepsForSegment)}
                      >
                        Create Segment
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" className="text-green-700 h-6 px-2" onClick={() => setSelectedStepsForSegment([])}>
                      Clear
                    </Button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Insert Segment */}
            {testSegments && testSegments.length > 0 && (
              <div className="mt-3">
                <select
                  className="w-full p-2 text-sm border border-gray-200 rounded-md bg-white"
                  onChange={(e) => {
                    if (e.target.value && onInsertSegment) {
                      onInsertSegment(e.target.value, steps.length);
                      e.target.value = '';
                    }
                  }}
                >
                  <option value="">Insert segment...</option>
                  {testSegments.map(segment => (
                    <option key={segment.id} value={segment.id}>
                      {segment.name} ({segment.step_count} steps)
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Steps List */}
          <div className="flex-1 overflow-y-auto p-3">
            {steps.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Code className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p className="font-medium">No steps yet</p>
                <p className="text-sm mt-1">Click "Add Step" to create your first step</p>
              </div>
            ) : (
              <div className="space-y-2">
                {steps.map((step, index) => {
                  // Count fallbacks - handle both formats
                  const fallbackCount = step.selector?.fallback_selectors?.length || 
                    (step.selectors?.length > 1 ? step.selectors.length - 1 : 0);
                  
                  return (
                  <div
                    key={step.id || index}
                    className={`
                      group flex items-start p-3 rounded-lg cursor-pointer transition-all border
                      ${selectedStepIndex === index 
                        ? 'bg-blue-50 border-blue-300 shadow-sm' 
                        : 'bg-white border-gray-100 hover:bg-gray-50 hover:border-gray-200'
                      }
                    `}
                    onClick={() => setSelectedStepIndex(index)}
                  >
                    {/* Checkbox for segment selection */}
                    <input
                      type="checkbox"
                      className="mt-1 mr-2 h-4 w-4 rounded border-gray-300 text-blue-600"
                      checked={selectedStepsForSegment.includes(index)}
                      onChange={(e) => {
                        e.stopPropagation();
                        toggleStepSelection(index);
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                    
                    {/* Move buttons */}
                    <div className="flex flex-col mr-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        className="p-0.5 text-gray-400 hover:text-blue-600 disabled:opacity-30"
                        onClick={(e) => { e.stopPropagation(); moveStepUp(index); }}
                        disabled={index === 0}
                        title="Move up"
                      >
                        <span className="text-xs">▲</span>
                      </button>
                      <button
                        className="p-0.5 text-gray-400 hover:text-blue-600 disabled:opacity-30"
                        onClick={(e) => { e.stopPropagation(); moveStepDown(index); }}
                        disabled={index === steps.length - 1}
                        title="Move down"
                      >
                        <span className="text-xs">▼</span>
                      </button>
                    </div>
                    
                    <div className="flex items-start space-x-2 flex-1 min-w-0">
                      <span className="text-xs font-mono text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">{index + 1}</span>
                      <div className="mt-0.5">{getStepIcon(step)}</div>
                      <div className="flex-1 min-w-0">
                        <span className="text-sm font-medium text-gray-900 block truncate">
                          {getStepDescription(step)}
                        </span>
                        {fallbackCount > 0 && (
                          <span className="text-xs text-amber-600">
                            +{fallbackCount} fallback{fallbackCount > 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                    </div>
                    <ChevronRight className={`h-4 w-4 text-gray-400 transition-transform mt-1 ${selectedStepIndex === index ? 'rotate-90' : ''}`} />
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Right Content: Step Details */}
        <div className="flex-1 overflow-y-auto bg-white">
          {selectedStep ? (
            <div className="p-6 max-w-3xl">
              {/* Step Header */}
              <div className="flex items-center justify-between mb-6 pb-4 border-b">
                <div className="flex items-center space-x-3">
                  <div className="p-3 bg-gray-100 rounded-xl">
                    {getStepIcon(selectedStep)}
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">Step {selectedStepIndex + 1}</h2>
                    <p className="text-sm text-gray-500 capitalize">{selectedStep.type || selectedStep.action || 'Custom Step'}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-1">
                  {/* Continue Recording from this step (for vnc-recorded tests) */}
                  {test?.tags?.includes('vnc-recorded') && onResumeVNCRecording && (
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => {
                        // Find the URL for this step or the nearest navigate step before it
                        let urlForStep = '';
                        for (let i = selectedStepIndex; i >= 0; i--) {
                          const step = steps[i];
                          if (step.action === 'navigate' || step.type === 'navigation') {
                            urlForStep = step.value || step.url || step.selector?.value || '';
                            break;
                          }
                        }
                        // Pass the test with steps up to and including this step
                        onResumeVNCRecording({
                          ...test,
                          steps: steps.slice(0, selectedStepIndex + 1),
                          resumeFromStepIndex: selectedStepIndex,
                          resumeUrl: urlForStep
                        });
                      }}
                      title="Continue recording from this step"
                      className="h-8 px-2 text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50"
                    >
                      <Monitor className="h-4 w-4 mr-1" />
                      <span className="text-xs">Continue</span>
                    </Button>
                  )}
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => duplicateStep(selectedStepIndex)}
                    title="Duplicate"
                    className="h-8 w-8 p-0"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={() => handleDeleteStep(selectedStepIndex)}
                    title="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Step Details Form */}
              <div className="space-y-6">
                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Step Description</label>
                  <Input
                    value={selectedStep.description || ''}
                    onChange={(e) => handleUpdateStep(selectedStepIndex, 'description', e.target.value)}
                    placeholder="Describe what this step does..."
                    className="text-base"
                  />
                </div>

                {/* Navigation URL - show for navigation steps or steps that look like navigation */}
                {(selectedStep.type === 'navigation' || selectedStep.action === 'navigate' || selectedStep.action === 'goto' ||
                  /^(go to|navigate to|open|visit)\s/i.test(selectedStep.description || '') ||
                  /^https?:\/\//i.test(selectedStep.value || '')) && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Globe className="h-4 w-4 inline mr-2 text-blue-500" />
                      Target URL
                    </label>
                    <Input
                      value={selectedStep.url || selectedStep.value || selectedStep.target_url || ''}
                      onChange={(e) => {
                        handleUpdateStep(selectedStepIndex, 'url', e.target.value);
                        handleUpdateStep(selectedStepIndex, 'value', e.target.value);
                        // Also set the action/type to navigate for proper handling
                        if (!selectedStep.action || selectedStep.action === 'custom') {
                          handleUpdateStep(selectedStepIndex, 'action', 'navigate');
                          handleUpdateStep(selectedStepIndex, 'type', 'navigation');
                        }
                      }}
                      placeholder="https://example.com"
                      className="font-mono text-sm"
                    />
                    {(selectedStep.url || selectedStep.value || '').includes('{{') && (
                      <p className="text-xs text-blue-600 mt-1">🔗 Using environment variable</p>
                    )}
                  </div>
                )}

                {/* Wait for URL - show for wait_for_url action */}
                {(selectedStep.action === 'wait_for_url' || selectedStep.type === 'wait_for_url') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Clock className="h-4 w-4 inline mr-2 text-yellow-500" />
                      Expected URL (wait until page loads this URL)
                    </label>
                    <Input
                      value={selectedStep.value || ''}
                      onChange={(e) => handleUpdateStep(selectedStepIndex, 'value', e.target.value)}
                      placeholder="https://example.com/expected-page or partial match"
                      className="font-mono text-sm"
                    />
                    <p className="text-xs text-gray-500 mt-1">The test will wait until the URL contains or matches this value</p>
                  </div>
                )}

                {/* Selector Section - Show for ALL steps EXCEPT navigation/wait/screenshot/wait_for_url */}
                {(() => {
                  // Detect if this is a navigation step (by action, type, or content)
                  const isNavigation = 
                    selectedStep.action === 'navigate' || 
                    selectedStep.action === 'goto' ||
                    selectedStep.type === 'navigation' ||
                    /^(go to|navigate to|open|visit)\s/i.test(selectedStep.description || '') ||
                    /^https?:\/\//i.test(selectedStep.value || '') ||
                    /^https?:\/\//i.test(selectedStep.url || '');
                  
                  const isWaitOrScreenshot = selectedStep.action === 'wait' || selectedStep.action === 'screenshot' || selectedStep.action === 'wait_for_url';
                  
                  // Show selector for any step that's NOT navigation/wait/screenshot/wait_for_url
                  return !isNavigation && !isWaitOrScreenshot;
                })() && (
                  <div className="space-y-4">
                    <div className="bg-gray-50 p-4 rounded-lg border">
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        <Target className="h-4 w-4 inline mr-2 text-green-500" />
                        Web Element Selector
                      </label>
                      
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">Selector Type</label>
                          <select
                            className="w-full p-2 border border-gray-300 rounded-md text-sm"
                            value={selectedStep.selector?.type || 'css'}
                            onChange={(e) => handleUpdateSelector(selectedStepIndex, 'type', e.target.value)}
                          >
                            <option value="css">CSS Selector</option>
                            <option value="xpath">XPath</option>
                            <option value="text">Text Content</option>
                            <option value="placeholder">Placeholder</option>
                            <option value="label">Label Text</option>
                            <option value="role">ARIA Role</option>
                          </select>
                        </div>
                        
                        <div className="col-span-2">
                          <label className="block text-xs font-medium text-gray-500 mb-1">Selector Value</label>
                          <Input
                            placeholder={`Enter ${selectedStep.selector?.type || 'css'} selector...`}
                            value={selectedStep.selector?.value || (typeof selectedStep.selector === 'string' ? selectedStep.selector : '')}
                            onChange={(e) => handleUpdateSelector(selectedStepIndex, 'value', e.target.value)}
                            className="font-mono text-sm"
                          />
                        </div>
                      </div>
                      
                      {/* Selector Examples */}
                      <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                        <p className="text-xs font-medium text-blue-800 mb-2">💡 Examples for {selectedStep.selector?.type || 'css'}:</p>
                        <div className="grid grid-cols-2 gap-1">
                          {getSelectorExamples(selectedStep.selector?.type || 'css').map((ex, i) => (
                            <div key={i} className="text-xs text-blue-700">
                              <code className="bg-white px-1 rounded">{ex.code}</code>
                              <span className="text-blue-600 ml-1">- {ex.desc}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                    
                    {/* Live Recording Captured Selectors (if available) */}
                    {selectedStep.selectors && selectedStep.selectors.length > 1 && (
                      <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                        <div className="flex items-center justify-between mb-3">
                          <label className="block text-sm font-medium text-green-800">
                            <CheckCircle className="h-4 w-4 inline mr-2" />
                            Captured Selectors (from Live Recording)
                          </label>
                          <Badge variant="secondary" className="bg-green-100 text-green-700">
                            {selectedStep.selectors.length} selectors
                          </Badge>
                        </div>
                        <p className="text-xs text-green-700 mb-3">
                          These selectors were automatically captured during live recording
                        </p>
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {selectedStep.selectors.map((sel, selIndex) => (
                            <div key={selIndex} className="flex items-center space-x-2 bg-white p-2 rounded-lg border border-green-100">
                              <Badge variant="secondary" className={`text-xs ${selIndex === 0 ? 'bg-green-200 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                                {selIndex === 0 ? 'Primary' : `#${selIndex}`}
                              </Badge>
                              <span className="text-xs px-1.5 py-0.5 bg-gray-100 rounded font-medium">{sel.type}</span>
                              <code className="flex-1 text-xs font-mono text-gray-700 truncate" title={sel.value}>
                                {sel.value}
                              </code>
                              {sel.confidence && (
                                <span className="text-xs text-gray-500">{Math.round(sel.confidence * 100)}%</span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Fallback Selectors (manual) */}
                    {selectedStep.type !== 'assertion' && (
                    <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                      <div className="flex items-center justify-between mb-3">
                        <label className="block text-sm font-medium text-amber-800">
                          <AlertTriangle className="h-4 w-4 inline mr-2" />
                          Manual Fallback Selectors
                        </label>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleAddFallback(selectedStepIndex)}
                          className="h-7 text-xs border-amber-300 text-amber-700 hover:bg-amber-100"
                        >
                          <Plus className="h-3 w-3 mr-1" />
                          Add Fallback
                        </Button>
                      </div>
                      <p className="text-xs text-amber-700 mb-3">
                        Additional backup selectors used when other selectors fail
                      </p>
                      
                      {selectedStep.selector?.fallback_selectors?.length > 0 ? (
                        <div className="space-y-2">
                          {selectedStep.selector.fallback_selectors.map((fallback, fbIndex) => (
                            <div key={fbIndex} className="flex items-center space-x-2 bg-white p-2 rounded-lg border border-amber-100">
                              <Badge variant="secondary" className="text-xs bg-amber-100 text-amber-700">#{fbIndex + 1}</Badge>
                              <select
                                className="p-1.5 border border-gray-300 rounded text-xs"
                                value={fallback.type}
                                onChange={(e) => handleUpdateFallback(selectedStepIndex, fbIndex, 'type', e.target.value)}
                              >
                                <option value="css">CSS</option>
                                <option value="xpath">XPath</option>
                                <option value="text">Text</option>
                                <option value="placeholder">Placeholder</option>
                                <option value="label">Label</option>
                                <option value="role">Role</option>
                              </select>
                              <Input
                                placeholder={`Fallback ${fallback.type} selector...`}
                                value={fallback.value}
                                onChange={(e) => handleUpdateFallback(selectedStepIndex, fbIndex, 'value', e.target.value)}
                                className="flex-1 text-xs font-mono h-8"
                              />
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleRemoveFallback(selectedStepIndex, fbIndex)}
                                className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-4 text-amber-600 text-sm">
                          No manual fallback selectors added
                        </div>
                      )}
                    </div>
                    )}
                  </div>
                )}

                {/* Value for type/fill actions - only show for text input steps */}
                {(selectedStep.action === 'type' || selectedStep.action === 'fill' || 
                  selectedStep.action === 'input' || selectedStep.action === 'enter_text') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Type className="h-4 w-4 inline mr-2 text-purple-500" />
                      Value to Type
                    </label>
                    
                    {/* Show variable tags if using variables */}
                    {selectedStep.value && selectedStep.value.includes('{{') && (
                      <div className="mb-2">
                        <span className="text-xs text-gray-500 mr-2">Using variable(s):</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {(selectedStep.value.match(/\{\{[^}]+\}\}/g) || []).map((varMatch, idx) => {
                            const varName = varMatch.replace(/\{\{|\}\}/g, '');
                            return (
                              <span key={idx} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-300">
                                <Database className="h-3 w-3 mr-1" />
                                {varMatch}
                                {testVariables && testVariables[varName] && (
                                  <span className="ml-1 text-yellow-600">= "{testVariables[varName]?.substring(0, 15)}{testVariables[varName]?.length > 15 ? '...' : ''}"</span>
                                )}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    
                    <div className="flex space-x-2">
                      <Input
                        value={selectedStep.value || ''}
                        onChange={(e) => handleUpdateStep(selectedStepIndex, 'value', e.target.value)}
                        placeholder="Text to type or select a variable..."
                        className={`text-base flex-1 ${selectedStep.value?.includes('{{') ? 'bg-yellow-50 border-yellow-300' : ''}`}
                      />
                      {/* Variable dropdown selector */}
                      {testVariables && Object.keys(testVariables).length > 0 && (
                        <select
                          className="p-2 border border-purple-300 rounded-md text-sm bg-purple-50 text-purple-700 font-medium min-w-[160px]"
                          value=""
                          onChange={(e) => {
                            if (e.target.value) {
                              const currentValue = selectedStep.value || '';
                              handleUpdateStep(selectedStepIndex, 'value', currentValue + `{{${e.target.value}}}`);
                              e.target.value = '';
                            }
                          }}
                        >
                          <option value="">+ Insert Variable</option>
                          {Object.entries(testVariables).map(([varName, varValue]) => (
                            <option key={varName} value={varName}>
                              {`{{${varName}}}`} = {varValue?.substring(0, 20)}{varValue?.length > 20 ? '...' : ''}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Use <code className="bg-gray-100 px-1 rounded">{'{{variableName}}'}</code> to reference test variables, or select from dropdown
                    </p>
                    
                    {/* Quick variable buttons */}
                    {testVariables && Object.keys(testVariables).length > 0 && (
                      <div className="mt-2 p-3 bg-purple-50 rounded-lg border border-purple-100">
                        <p className="text-xs font-medium text-purple-800 mb-2">Quick Insert Variables:</p>
                        <div className="flex flex-wrap gap-2">
                          {Object.entries(testVariables).map(([varName, varValue]) => (
                            <button
                              key={varName}
                              type="button"
                              className="text-xs bg-white px-3 py-1.5 rounded-md border border-purple-200 text-purple-700 hover:bg-purple-100 hover:border-purple-300 transition-colors flex items-center gap-1"
                              onClick={() => {
                                const currentValue = selectedStep.value || '';
                                handleUpdateStep(selectedStepIndex, 'value', currentValue + `{{${varName}}}`);
                              }}
                              title={`Value: ${varValue}`}
                            >
                              <span className="font-mono">{`{{${varName}}}`}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* No variables hint */}
                    {(!testVariables || Object.keys(testVariables).length === 0) && (
                      <div className="mt-2 p-2 bg-gray-50 rounded-lg border border-gray-200">
                        <p className="text-xs text-gray-600">
                          💡 No variables defined yet. Click <strong>"Variables"</strong> button at the top to add reusable values like usernames, passwords, URLs.
                        </p>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Select/Dropdown value - for select actions */}
                {(selectedStep.action === 'select' || selectedStep.action === 'select_option') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <ChevronDown className="h-4 w-4 inline mr-2 text-blue-500" />
                      Option to Select
                    </label>
                    
                    {/* Show variable tag if using a variable */}
                    {selectedStep.value && selectedStep.value.includes('{{') && (
                      <div className="mb-2 flex items-center gap-2">
                        <span className="text-xs text-gray-500">Using variable:</span>
                        <span className="inline-flex items-center px-2.5 py-1 rounded-md text-sm font-medium bg-yellow-100 text-yellow-800 border border-yellow-300">
                          <Database className="h-3 w-3 mr-1" />
                          {selectedStep.value}
                          {testVariables && testVariables[selectedStep.value.replace(/\{\{|\}\}/g, '')] && (
                            <span className="ml-2 text-yellow-600 text-xs">
                              = "{testVariables[selectedStep.value.replace(/\{\{|\}\}/g, '')]}"
                            </span>
                          )}
                        </span>
                        <button
                          type="button"
                          className="text-xs text-red-600 hover:text-red-700 underline"
                          onClick={() => handleUpdateStep(selectedStepIndex, 'value', '')}
                        >
                          Clear
                        </button>
                      </div>
                    )}
                    
                    <div className="flex space-x-2">
                      <Input
                        value={selectedStep.value || ''}
                        onChange={(e) => handleUpdateStep(selectedStepIndex, 'value', e.target.value)}
                        placeholder="Option value, text, or variable..."
                        className={`text-base flex-1 ${selectedStep.value?.includes('{{') ? 'bg-yellow-50 border-yellow-300' : ''}`}
                      />
                      {/* Variable dropdown selector */}
                      {testVariables && Object.keys(testVariables).length > 0 && (
                        <select
                          className="p-2 border border-blue-300 rounded-md text-sm bg-blue-50 text-blue-700 font-medium min-w-[160px]"
                          value=""
                          onChange={(e) => {
                            if (e.target.value) {
                              handleUpdateStep(selectedStepIndex, 'value', `{{${e.target.value}}}`);
                              e.target.value = '';
                            }
                          }}
                        >
                          <option value="">+ Insert Variable</option>
                          {Object.entries(testVariables).map(([varName, varValue]) => (
                            <option key={varName} value={varName}>
                              {`{{${varName}}}`} = {varValue?.substring(0, 20)}{varValue?.length > 20 ? '...' : ''}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Enter the option value, label, index, or use a variable like <code className="bg-gray-100 px-1 rounded">{'{{variableName}}'}</code>
                    </p>
                    
                    {/* Quick variable buttons */}
                    {testVariables && Object.keys(testVariables).length > 0 && (
                      <div className="mt-2 p-3 bg-blue-50 rounded-lg border border-blue-100">
                        <p className="text-xs font-medium text-blue-800 mb-2">Quick Insert Variables:</p>
                        <div className="flex flex-wrap gap-2">
                          {Object.entries(testVariables).map(([varName, varValue]) => (
                            <button
                              key={varName}
                              type="button"
                              className="text-xs bg-white px-3 py-1.5 rounded-md border border-blue-200 text-blue-700 hover:bg-blue-100 hover:border-blue-300 transition-colors flex items-center gap-1"
                              onClick={() => {
                                handleUpdateStep(selectedStepIndex, 'value', `{{${varName}}}`);
                              }}
                              title={`Value: ${varValue}`}
                            >
                              <span className="font-mono">{`{{${varName}}}`}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* No variables hint */}
                    {(!testVariables || Object.keys(testVariables).length === 0) && (
                      <div className="mt-2 p-2 bg-gray-50 rounded-lg border border-gray-200">
                        <p className="text-xs text-gray-600">
                          💡 No variables defined yet. Click <strong>"Variables"</strong> button at the top to add reusable dropdown options.
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Assertion specific fields */}
                {/* Assertion step editing - check both type and action */}
                {(selectedStep.type === 'assertion' || selectedStep.action?.startsWith('assert_')) && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Assertion Type</label>
                      <select
                        className="w-full p-2 border border-gray-300 rounded-md"
                        value={selectedStep.assertion_type || (selectedStep.action === 'assert_value' ? 'value' : selectedStep.action === 'assert_visible' ? 'visible' : selectedStep.action === 'assert_exists' ? 'exists' : 'text')}
                        onChange={(e) => {
                          // Update both assertion_type and action
                          const actionMap = {
                            'text': 'assert_text',
                            'value': 'assert_value',
                            'visible': 'assert_visible',
                            'exists': 'assert_exists',
                            'attribute': 'assert_attribute'
                          };
                          handleUpdateStep(selectedStepIndex, 'assertion_type', e.target.value);
                          handleUpdateStep(selectedStepIndex, 'action', actionMap[e.target.value] || 'assert_text');
                        }}
                      >
                        <option value="text">Validate Text</option>
                        <option value="value">Validate Value</option>
                        <option value="visible">Validate Visible</option>
                        <option value="exists">Validate Exists</option>
                        <option value="attribute">Validate Attribute</option>
                      </select>
                    </div>
                    
                    {/* Expected Text - show for text/value assertions */}
                    {(selectedStep.assertion_type === 'text' || selectedStep.assertion_type === 'value' || 
                      selectedStep.action === 'assert_text' || selectedStep.action === 'assert_value' ||
                      (!selectedStep.assertion_type && !selectedStep.action?.includes('visible') && !selectedStep.action?.includes('exists'))) && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {(selectedStep.assertion_type === 'value' || selectedStep.action === 'assert_value') ? 'Expected Value' : 'Expected Text'}
                        </label>
                        
                        {/* Show variable tags if expected_text contains variables */}
                        {selectedStep.expected_text?.includes('{{') && (
                          <div className="mb-2 p-2 bg-yellow-50 rounded-lg border border-yellow-200">
                            <p className="text-xs font-medium text-yellow-800 mb-1">Using Variables:</p>
                            <div className="flex flex-wrap gap-1">
                              {selectedStep.expected_text.match(/\{\{([^}]+)\}\}/g)?.map((match, idx) => {
                                const varName = match.replace(/\{\{|\}\}/g, '');
                                return (
                                  <span key={idx} className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full font-mono">
                                    {match}
                                    {testVariables && testVariables[varName] && (
                                      <span className="ml-1 text-yellow-600">= "{testVariables[varName]?.substring(0, 15)}{testVariables[varName]?.length > 15 ? '...' : ''}"</span>
                                    )}
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        
                        <div className="flex gap-2 items-center">
                          <Input
                            value={selectedStep.expected_text || ''}
                            onChange={(e) => handleUpdateStep(selectedStepIndex, 'expected_text', e.target.value)}
                            placeholder={(selectedStep.assertion_type === 'value' || selectedStep.action === 'assert_value') ? 'Expected value to validate' : 'Expected text to find in element'}
                            className={`flex-1 ${selectedStep.expected_text?.includes('{{') ? 'bg-yellow-50 border-yellow-300' : 'border-green-300 focus:border-green-500'}`}
                          />
                          {/* Variable dropdown selector */}
                          {testVariables && Object.keys(testVariables).length > 0 && (
                            <select
                              className="p-2 border border-purple-300 rounded-md text-sm bg-purple-50 text-purple-700 font-medium min-w-[160px]"
                              value=""
                              onChange={(e) => {
                                if (e.target.value) {
                                  const currentValue = selectedStep.expected_text || '';
                                  handleUpdateStep(selectedStepIndex, 'expected_text', currentValue + `{{${e.target.value}}}`);
                                  e.target.value = '';
                                }
                              }}
                            >
                              <option value="">+ Insert Variable</option>
                              {Object.entries(testVariables).map(([varName, varValue]) => (
                                <option key={varName} value={varName}>
                                  {`{{${varName}}}`} = {varValue?.substring(0, 20)}{varValue?.length > 20 ? '...' : ''}
                                </option>
                              ))}
                            </select>
                          )}
                        </div>
                        
                        <p className="text-xs text-gray-500 mt-1">
                          💡 Use <code className="bg-gray-100 px-1 rounded">{'{{variableName}}'}</code> to reference test variables
                        </p>
                        
                        {/* Quick variable buttons for assertion */}
                        {testVariables && Object.keys(testVariables).length > 0 && (
                          <div className="mt-2 p-3 bg-purple-50 rounded-lg border border-purple-100">
                            <p className="text-xs font-medium text-purple-800 mb-2">Quick Insert Variables:</p>
                            <div className="flex flex-wrap gap-2">
                              {Object.entries(testVariables).map(([varName, varValue]) => (
                                <button
                                  key={varName}
                                  type="button"
                                  className="text-xs bg-white px-3 py-1.5 rounded-md border border-purple-200 text-purple-700 hover:bg-purple-100 hover:border-purple-300 transition-colors flex items-center gap-1"
                                  onClick={() => {
                                    const currentValue = selectedStep.expected_text || '';
                                    handleUpdateStep(selectedStepIndex, 'expected_text', currentValue + `{{${varName}}}`);
                                  }}
                                  title={`Value: ${varValue}`}
                                >
                                  <span className="font-mono">{`{{${varName}}}`}</span>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Captured value display */}
                    {selectedStep.captured_value && (
                      <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                        <label className="block text-sm font-medium text-green-800 mb-1">
                          <CheckCircle className="h-4 w-4 inline mr-2" />
                          Captured Value (from recording)
                        </label>
                        <p className="text-sm text-green-700 font-mono bg-white p-2 rounded border border-green-100">{selectedStep.captured_value}</p>
                        <p className="text-xs text-green-600 mt-2">
                          💡 This was the value captured during live recording. You can copy it to "Expected Text" above.
                        </p>
                        {!selectedStep.expected_text && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="mt-2 text-green-700 border-green-300 hover:bg-green-100"
                            onClick={() => handleUpdateStep(selectedStepIndex, 'expected_text', selectedStep.captured_value)}
                          >
                            Use Captured Value as Expected
                          </Button>
                        )}
                      </div>
                    )}
                  </>
                )}

                {/* Screenshot name */}
                {selectedStep.type === 'screenshot' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Camera className="h-4 w-4 inline mr-2 text-pink-500" />
                      Screenshot Name
                    </label>
                    <Input
                      value={selectedStep.name || ''}
                      onChange={(e) => handleUpdateStep(selectedStepIndex, 'name', e.target.value)}
                      placeholder="screenshot_name"
                    />
                  </div>
                )}

                {/* Timeout Slider */}
                <div className="bg-gray-50 p-4 rounded-lg border">
                  <div className="flex items-center justify-between mb-3">
                    <label className="block text-sm font-medium text-gray-700">
                      <Clock className="h-4 w-4 inline mr-2 text-yellow-500" />
                      Step Timeout
                    </label>
                    <span className="text-lg font-bold text-blue-600">
                      {((selectedStep.timeout || 30000) / 1000).toFixed(0)}s
                    </span>
                  </div>
                  
                  <input
                    type="range"
                    min="4"
                    max="120"
                    step="1"
                    value={(selectedStep.timeout || 30000) / 1000}
                    onChange={(e) => handleUpdateStep(selectedStepIndex, 'timeout', parseInt(e.target.value) * 1000)}
                    className="w-full h-2 bg-gray-300 rounded-lg appearance-none cursor-pointer"
                    style={{
                      background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${(((selectedStep.timeout || 30000) / 1000 - 4) / (120 - 4)) * 100}%, #d1d5db ${(((selectedStep.timeout || 30000) / 1000 - 4) / (120 - 4)) * 100}%, #d1d5db 100%)`
                    }}
                  />
                  
                  <div className="flex justify-between text-xs text-gray-500 mt-2">
                    <span>4s (Fast)</span>
                    <span>30s (Default)</span>
                    <span>120s (Slow)</span>
                  </div>
                  
                  <p className="text-xs text-gray-500 mt-3 p-2 bg-white rounded">
                    💡 Use shorter timeouts (4-10s) for quick elements. Use longer timeouts (30-120s) for slow loading content.
                  </p>
                </div>

                {/* Raw Step Data */}
                <details className="border rounded-lg">
                  <summary className="p-3 cursor-pointer text-sm font-medium text-gray-600 hover:bg-gray-50 rounded-lg">
                    <Code className="h-4 w-4 inline mr-2" />
                    View Raw Step Data
                  </summary>
                  <pre className="p-4 bg-gray-900 text-green-400 text-xs overflow-auto max-h-64 font-mono rounded-b-lg">
                    {JSON.stringify(selectedStep, null, 2)}
                  </pre>
                </details>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full text-gray-500">
              <div className="text-center">
                <MousePointer className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                <h3 className="text-lg font-medium text-gray-600">Select a step to edit</h3>
                <p className="text-sm mt-1 text-gray-400">Click on a step in the sidebar to view and edit its details</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add Step Modal */}
      <Dialog open={showAddStepModal} onOpenChange={setShowAddStepModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add New Step</DialogTitle>
            <DialogDescription>
              Use AI to generate a step or add a blank step manually.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Quick Add Buttons */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Quick Add</label>
              <div className="flex flex-wrap gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    const blankStep = {
                      id: `step_${Date.now()}`,
                      type: 'custom',
                      action: 'click',
                      description: 'New step',
                      selector: { type: 'css', value: '', fallback_selectors: [] },
                      value: '',
                      wait_before: 0,
                      wait_after: 500,
                      timeout: 30000,
                      order: steps.length + 1
                    };
                    setSteps([...steps, blankStep]);
                    setShowAddStepModal(false);
                    setSelectedStepIndex(steps.length);
                  }}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Blank Step
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    const waitStep = {
                      id: `step_${Date.now()}`,
                      type: 'wait_for_url',
                      action: 'wait_for_url',
                      description: 'Wait for page to load',
                      value: '',
                      wait_before: 0,
                      wait_after: 500,
                      timeout: 30000,
                      order: steps.length + 1
                    };
                    setSteps([...steps, waitStep]);
                    setShowAddStepModal(false);
                    setSelectedStepIndex(steps.length);
                  }}
                >
                  <Clock className="h-4 w-4 mr-1" />
                  Wait for URL
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    const screenshotStep = {
                      id: `step_${Date.now()}`,
                      type: 'screenshot',
                      action: 'screenshot',
                      description: 'Take screenshot',
                      wait_before: 500,
                      wait_after: 500,
                      timeout: 30000,
                      order: steps.length + 1
                    };
                    setSteps([...steps, screenshotStep]);
                    setShowAddStepModal(false);
                    setSelectedStepIndex(steps.length);
                  }}
                >
                  <Camera className="h-4 w-4 mr-1" />
                  Screenshot
                </Button>
              </div>
            </div>
            
            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-gray-500">Or generate with AI</span>
              </div>
            </div>
            
            {/* AI Generation */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Describe the step</label>
              <Textarea
                value={newStepPrompt}
                onChange={(e) => setNewStepPrompt(e.target.value)}
                placeholder="Examples:
• Click the submit button
• Type hello@example.com in the email field
• Verify the success message appears"
                rows={4}
                className="text-sm"
              />
            </div>
            
            {/* Actions */}
            <div className="flex justify-end space-x-2 pt-2">
              <Button variant="outline" onClick={() => setShowAddStepModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleAddStep} disabled={!newStepPrompt.trim() || addingStep}>
                {addingStep ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
                Generate with AI
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );

  // Use Portal to render outside the normal DOM hierarchy
  return ReactDOM.createPortal(editorContent, document.body);
};

export default FlowTestEditor;
